import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User as UserIcon, KeyRound, Loader2 } from "lucide-react";

function describe(detail) {
  if (!detail) return "Error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Account() {
  const { user, refresh } = useAuth();
  const [nombre, setNombre] = useState(user?.nombre || "");
  const [email, setEmail] = useState(user?.email || "");
  const [currPw, setCurrPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/auth/me", { nombre: nombre.trim(), email: email.trim() });
      toast.success("Perfil actualizado");
      refresh();
    } catch (err) {
      toast.error(describe(err?.response?.data?.detail));
    } finally { setSavingProfile(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("La contraseña debe tener al menos 8 caracteres"); return; }
    if (newPw !== newPw2) { toast.error("Las contraseñas no coinciden"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currPw, new_password: newPw });
      toast.success("Contraseña actualizada");
      setCurrPw(""); setNewPw(""); setNewPw2("");
    } catch (err) {
      toast.error(describe(err?.response?.data?.detail));
    } finally { setSavingPw(false); }
  };

  return (
    <div className="space-y-8" data-testid="account-page">
      <header>
        <div className="uppercase-label">Mi cuenta</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
          <UserIcon className="w-9 h-9 text-[#8B5A33]" strokeWidth={1.5} /> Administración
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Actualiza tus datos y cambia tu contraseña.</p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <form onSubmit={saveProfile} className="bg-white border border-border rounded-lg p-6 space-y-4" data-testid="profile-form">
          <h2 className="font-display text-2xl">Perfil</h2>
          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="account-nombre" />
          </div>
          <div className="space-y-2">
            <Label>Correo</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email" />
          </div>
          <Button type="submit" className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" disabled={savingProfile} data-testid="account-save-profile">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Guardar perfil
          </Button>
        </form>

        <form onSubmit={changePw} className="bg-white border border-border rounded-lg p-6 space-y-4" data-testid="password-form">
          <h2 className="font-display text-2xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#8B5A33]" /> Cambiar contraseña</h2>
          <div className="space-y-2">
            <Label>Contraseña actual</Label>
            <Input type="password" value={currPw} onChange={(e) => setCurrPw(e.target.value)} required data-testid="account-currpw" />
          </div>
          <div className="space-y-2">
            <Label>Contraseña nueva</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required data-testid="account-newpw" />
          </div>
          <div className="space-y-2">
            <Label>Confirmar contraseña nueva</Label>
            <Input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} required data-testid="account-newpw2" />
          </div>
          <Button type="submit" className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" disabled={savingPw} data-testid="account-save-pw">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Cambiar contraseña
          </Button>
        </form>
      </section>
    </div>
  );
}
